import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_CONFIG } from "../../../resources/api";

import { IPayAll, IPayToken, IResponseData } from "./types";

const YOOKASSA_API_URL = "https://api.yookassa.ru/v3";

const getYooKassaAuth = (): string => {
  const credentials = `${API_CONFIG.KASSA_MERCHANT_ID}:${API_CONFIG.KASSA_SECRET}`;
  return `Basic ${btoa(credentials)}`;
};

const generateIdempotenceKey = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const fetchGetToken = createAsyncThunk<IPayToken, { email?: string; phone?: string; returnUrl?: string } | void>(
  "getToken/fetchGetToken",
  async (params, { rejectWithValue }) => {
    try {
      const idempotenceKey = generateIdempotenceKey();
      const userPhone = params && typeof params === 'object' && 'phone' in params ? params.phone : undefined;
      const returnUrl = params && typeof params === 'object' && 'returnUrl' in params 
        ? params.returnUrl 
        : `${window.location.origin}/profile/add-card`;
      
      const paymentData: any = {
        amount: {
          value: "11.00",
          currency: "RUB"
        },
        confirmation: {
          type: "redirect",
          return_url: returnUrl
        },
        save_payment_method: true,
        payment_method_data: {
          type: "bank_card"
        },
        capture: true,
        description: "Привязка карты пользователя",
        receipt: {
          customer: {
            phone: userPhone
          },
          items: [
            {
              description: "Привязка карты",
              quantity: 1,
              amount: {
                value: "11.00",
                currency: "RUB"
              },
              vat_code: 1,
              payment_mode: "full_prepayment",
              payment_subject: "service"
            }
          ]
        }
      };

      const response = await axios.post(
        `${YOOKASSA_API_URL}/payments`,
        paymentData,
        {
          headers: {
            "Authorization": getYooKassaAuth(),
            "Idempotence-Key": idempotenceKey,
            "Content-Type": "application/json"
          }
        }
      );

      const payment = response.data;
      
      return {
        confirmation_token: payment.confirmation?.confirmation_url || "",
        payment_id: payment.id,
        webPayment_confirmation_token: "",
        webPayment_payment_id: ""
      };
    } catch (error: any) {
      console.log("error fetchGetToken >>>", error);
      return rejectWithValue(error.response?.data?.description || "Ошибка получения токена для добавления карты");
    }
  }
);

export const fetchGetWebPayToken = createAsyncThunk<IResponseData, IPayAll>(
  "getToken/fetchGetWebPayToken",
  async (data, { rejectWithValue }) => {
    try {
      const idempotenceKey = generateIdempotenceKey();
      const totalPrice = data.cartTotal?.totalPrice || 0;

      const paymentData = {
        amount: {
          value: totalPrice.toFixed(2),
          currency: "RUB"
        },
        capture: true,
        confirmation: {
          type: "redirect",
          return_url: `${window.location.origin}/payment-success`
        },
        description: "Оплата заказа"
      };

      const response = await axios.post(
        `${YOOKASSA_API_URL}/payments`,
        paymentData,
        {
          headers: {
            "Authorization": getYooKassaAuth(),
            "Idempotence-Key": idempotenceKey,
            "Content-Type": "application/json"
          }
        }
      );

      const payment = response.data;
      
      return {
        confirmation_token: payment.confirmation?.confirmation_url || "",
        payment_id: payment.id
      };
    } catch (error: any) {
      console.log("fetchGetWebPayToken error >>>", error);
      return rejectWithValue(error.response?.data?.description || "Ошибка получения токена для оплаты");
    }
  }
);

export interface IPaymentInfo {
  id: string;
  status: string;
  paid: boolean;
  payment_method?: {
    id: string;
    type: string;
    card?: {
      last4: string;
      expiry_month?: string;
      expiry_year?: string;
    };
  };
  amount: {
    value: string;
    currency: string;
  };
}

export const fetchGetPaymentInfo = createAsyncThunk<IPaymentInfo, string>(
  "paytoken/fetchGetPaymentInfo",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${YOOKASSA_API_URL}/payments/${paymentId}`,
        {
          headers: {
            "Authorization": getYooKassaAuth(),
            "Content-Type": "application/json"
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.log("error fetchGetPaymentInfo >>>", error);
      return rejectWithValue(error.response?.data?.description || "Ошибка получения информации о платеже");
    }
  }
);

export const fetchRefundPayment = createAsyncThunk<void, string>(
  "paytoken/fetchRefundPayment",
  async (paymentId, { rejectWithValue }) => {
    try {
      const idempotenceKey = generateIdempotenceKey();
      
      const refundData = {
        amount: {
          value: "11.00",
          currency: "RUB"
        },
        payment_id: paymentId
      };

      await axios.post(
        `${YOOKASSA_API_URL}/refunds`,
        refundData,
        {
          headers: {
            "Authorization": getYooKassaAuth(),
            "Idempotence-Key": idempotenceKey,
            "Content-Type": "application/json"
          }
        }
      );
    } catch (error: any) {
      console.log("error fetchRefundPayment >>>", error);
      return rejectWithValue(error.response?.data?.description || "Ошибка возврата платежа");
    }
  }
);